<script setup>
import { getDetail } from '@/api/UserGroup';
import { useSajuData } from '@/composables/saju-insight/useSajuData';
import { ANALYSIS_SAJU_CONTEXT, toAnalysisPerson } from '@/composables/saju-insight/useAnalysisSajuContext';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import HarmonyAnalysis from './HarmonyAnalysis.vue';
import NamingAnalysis from './NamingAnalysis.vue';
import AlertComponent from '@/components/common/Alert.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';

const props = defineProps({ sajuId: { type: [String, Number], required: true }, service: String, mobile: Boolean });
const router = useRouter();
const store = useAnalysisStore();
const dataStore = useAnalysisDataStore();
const primarySajuStore = usePrimarySajuStore();
const saju = ref(null);
const errorMessage = ref('');
const alertRef = ref(null);
let active = true;
const isActive = () => active;
const returnToSelection = () => router.push({
  path: '/saju-service',
  query: { menu: props.service === 'harmony' ? '/overviewNangmanHarmony' : '/overviewNangmanNaming' },
});
const { initializeSajuData } = useSajuData();

const resetServiceState = () => {
  store.setHarmonyPurchaseState(false, null);
  store.resetHarmonyPaymentData();
  store.resetHarmonyExtras();
  store.setHarmonySelectionState(null, 0);
  store.setNangmanHarmonyIndex(1);
  store.setSelectedContentIndex(null);
  store.setHarmonyExtrasAutoLoading(false);
  store.clearHarmonyCategoryPaymentTarget();
  store.clearHarmonyExtraPaymentTarget();
  dataStore.resetAll();
};
if (props.mobile) {
  store.resetAnalysisContext(props.sajuId);
  store.resetSelectedMenu();
}
resetServiceState();

const openHistory = (service, guestUserId, analysisLogId) => {
  if (!active || !Number.isInteger(Number(guestUserId)) || Number(guestUserId) <= 0) return;
  if (!Number.isInteger(Number(analysisLogId)) || Number(analysisLogId) <= 0) return;
  return router.push({
    path: props.mobile ? `/m/saju-insight/nangman-${service}` : `/analysis/${guestUserId}`,
    query: {
      ...(props.mobile ? { id: String(guestUserId) } : {}),
      menu: service === 'harmony' ? '/nangman-harmony' : '/saju-naming',
      analysisLogId: String(analysisLogId),
    },
  });
};

provide(ANALYSIS_SAJU_CONTEXT, { saju: readonly(saju), isActive, openHistory });

onBeforeUnmount(() => {
  active = false;
  resetServiceState();
  if (props.mobile) store.resetAnalysisContext();
});

onMounted(async () => {
  try {
    const id = Number(props.sajuId);
    if (!Number.isInteger(id) || id <= 0) throw new Error('유효하지 않은 사주 정보입니다.');
    // 내 사주 정보는 구독 권한 판정에만 사용하고, 서비스 기준은 URL의 id로 유지한다.
    const [detail] = await Promise.all([
      getDetail(id),
      primarySajuStore.mySajuId ? Promise.resolve() : primarySajuStore.fetchMySaju(),
    ]);
    if (!active) return;
    if (!detail || Number(detail.guestUserId) !== id) throw new Error('사주 상세정보를 찾을 수 없습니다.');
    const result = await initializeSajuData(id);
    if (!active) return;
    if (result?.failed) throw new Error(result.message);
    if (result?.unknownTime) throw new Error('시간 모름인 사주는\n상세분석 서비스 제공이 어렵습니다.');
    if (result?.futureSaju) throw new Error('내년 이후 사주는\n상세분석 서비스 제공이 어렵습니다.');
    saju.value = toAnalysisPerson(detail);
  } catch (error) {
    if (!active) return;
    errorMessage.value = error?.isAxiosError || error?.response
      ? '사주 정보를 불러올 수 없습니다. 사주를 다시 선택해주세요.'
      : error?.message || '사주 정보를 불러오지 못했습니다.';
    await nextTick();
    if (active) alertRef.value?.openAlertDialog();
  }
});
</script>

<template>
  <template v-if="saju">
    <slot>
      <HarmonyAnalysis v-if="service === 'harmony'" :saju="saju" />
      <NamingAnalysis v-else :saju="saju" />
    </slot>
  </template>
  <AnalysisLoadingOverlay
    v-else
    :loading="!errorMessage"
    :message="service === 'harmony' ? '낭만궁합 분석중입니다!' : '낭만작명 분석중입니다!'"
    contained
  />
  <AlertComponent ref="alertRef" :title="errorMessage" :nextUrl="returnToSelection" />
</template>
